import React, { ReactElement, useMemo, useRef, useState } from 'react'
import uiConfigStore from 'store/uiConfig/uiConfig'
import SearchField from 'ui/atoms/SearchField/SearchField'
import { useSnapshot } from 'valtio'
import { buildDataFlowConsistencySummary, buildDataFlowGraph } from './UiConfigDataFlowPanel.helpers'
import styles from './UiConfigDataFlowPanel.module.less'
import type {
  DataFlowCalcDependency,
  DataFlowEdge,
  DataFlowSource,
  DataFlowWarning,
} from './UiConfigDataFlowPanel.types'

type Props = {
  uid: UiConfigUid
}

type DataFlowEdgeRow = {
  id: string
  edge: DataFlowEdge
}

type DataFlowDependencyRow = {
  id: string
  dependency: DataFlowCalcDependency
}

function getWarningClassificationPriority(warning: DataFlowWarning): number {
  switch (warning.classification) {
    case 'runtime_failure':
      return 0
    case 'cleanup':
      return 1
    case 'draft_state':
      return 2
  }
}

function getWarningsPriority(warnings: DataFlowWarning[]): number {
  if (warnings.length === 0) {
    return 99
  }

  return Math.min(...warnings.map((warning) => getWarningClassificationPriority(warning)))
}

function getEdgeStatusPriority(edge: DataFlowEdge): number {
  switch (edge.status) {
    case 'unmatched_item':
      return 0
    case 'orphaned_dsi':
      return 1
    case 'matched':
      return 2
  }
}

function getDependencyStatusPriority(dependency: DataFlowCalcDependency): number {
  return dependency.status === 'missing' ? 0 : 1
}

function getEdgeStatusLabel(edge: DataFlowEdge): string {
  switch (edge.status) {
    case 'matched':
      return 'matched'
    case 'unmatched_item':
      return 'unmatched item'
    case 'orphaned_dsi':
      return 'orphaned DSI'
  }
}

function getEdgeStatusClassName(edge: DataFlowEdge): string {
  switch (edge.status) {
    case 'matched':
      return styles.UiConfigDataFlowPanel_StatusBadge__matched
    case 'unmatched_item':
      return styles.UiConfigDataFlowPanel_StatusBadge__unmatched
    case 'orphaned_dsi':
      return styles.UiConfigDataFlowPanel_StatusBadge__orphaned
  }
}

function getSourceLabel(source: DataFlowSource | null): string {
  switch (source) {
    case 'raw_config':
      return 'raw config'
    case 'template_resolution':
      return 'template resolution'
    case 'base_config':
      return 'base_config'
    case 'calc_output':
      return 'calc output'
    case 'unknown':
      return 'unknown'
    case null:
      return '—'
  }
}

function getDependencyStatusClassName(dependency: DataFlowCalcDependency): string {
  return dependency.status === 'resolved'
    ? styles.UiConfigDataFlowPanel_StatusBadge__matched
    : styles.UiConfigDataFlowPanel_StatusBadge__unmatched
}

function getWarningClassificationLabel(warning: DataFlowWarning): string {
  switch (warning.classification) {
    case 'runtime_failure':
      return 'runtime failure'
    case 'cleanup':
      return 'cleanup'
    case 'draft_state':
      return 'draft state'
  }
}

function getWarningSourceHintLabel(warning: DataFlowWarning): string | null {
  switch (warning.context.sourceHint) {
    case 'base_config':
      return 'Expected source: base_config'
    case 'code_template':
      return 'Expected source: code template'
    case 'raw_config':
      return 'Source: raw config'
    case 'template_resolution':
      return 'Source: resolved from template'
    case 'incomplete':
      return 'State: incomplete draft input'
    case 'unknown':
      return 'Source: no known match'
    default:
      return null
  }
}

function getIssuesBadgeLabel(count: number): string {
  return count === 1 ? '1 issue' : `${count} issues`
}

function edgeMatchesFilter(edge: DataFlowEdge, normalizedFilter: string): boolean {
  return [
    edge.itemDataId,
    edge.itemTitle,
    edge.dsiReturnId,
    edge.dsiType,
    edge.source ? getSourceLabel(edge.source) : null,
    getEdgeStatusLabel(edge),
  ].some((value) => typeof value === 'string' && value.toLowerCase().includes(normalizedFilter))
}

function dependencyMatchesFilter(dependency: DataFlowCalcDependency, normalizedFilter: string): boolean {
  return [
    dependency.fromReturnId,
    dependency.toReturnId,
    getSourceLabel(dependency.fromSource),
    dependency.status,
  ].some((value) => value.toLowerCase().includes(normalizedFilter))
}

function warningMatchesFilter(warning: DataFlowWarning, normalizedFilter: string): boolean {
  const sourceHintLabel = getWarningSourceHintLabel(warning)

  return [
    warning.kind.replaceAll('_', ' '),
    getWarningClassificationLabel(warning),
    warning.message,
    warning.context.dataId,
    warning.context.returnId,
    warning.context.itemTitle,
    warning.context.dsiType,
    warning.context.variable,
    sourceHintLabel,
  ]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLowerCase().includes(normalizedFilter))
}

function getWarningTargetRowId(
  warning: DataFlowWarning,
  edgeRows: DataFlowEdgeRow[],
  dependencyRows: DataFlowDependencyRow[]
): string | null {
  if (warning.kind === 'unmatched_item') {
    const match = edgeRows.find(
      ({ edge }) => edge.status === 'unmatched_item' && edge.itemDataId === warning.context.dataId
    )
    return match?.id ?? null
  }

  if (warning.kind === 'orphaned_dsi' || warning.kind === 'unresolved_template') {
    const match = edgeRows.find(({ edge }) => edge.dsiReturnId === warning.context.returnId)
    return match?.id ?? null
  }

  if (warning.kind === 'calc_missing_variable') {
    const match = dependencyRows.find(
      ({ dependency }) =>
        dependency.fromReturnId === warning.context.variable && dependency.toReturnId === warning.context.returnId
    )
    return match?.id ?? null
  }

  return null
}

function renderEdgeSide(primary: string | null, secondary?: string | null): ReactElement {
  if (!primary) {
    return <span className={styles.UiConfigDataFlowPanel_CardHint}>None</span>
  }

  return (
    <div>
      <div className={styles.UiConfigDataFlowPanel_Code}>{primary}</div>
      {secondary ? <div>{secondary}</div> : null}
    </div>
  )
}

function WarningItem({
  warning,
  onNavigate,
  canNavigate,
}: {
  warning: DataFlowWarning
  onNavigate?: () => void
  canNavigate: boolean
}): ReactElement {
  const sourceHintLabel = getWarningSourceHintLabel(warning)

  return (
    <div
      className={`${styles.UiConfigDataFlowPanel_Warning} ${styles[`UiConfigDataFlowPanel_Warning__${warning.severity}`]}`}
    >
      <div className={styles.UiConfigDataFlowPanel_WarningMeta}>
        <span className={styles.UiConfigDataFlowPanel_WarningKind}>{warning.kind.replaceAll('_', ' ')}</span>
        <span className={styles.UiConfigDataFlowPanel_WarningClassification}>
          {getWarningClassificationLabel(warning)}
        </span>
      </div>
      <div className={styles.UiConfigDataFlowPanel_WarningMessage}>{warning.message}</div>
      {sourceHintLabel ? <div className={styles.UiConfigDataFlowPanel_WarningHint}>{sourceHintLabel}</div> : null}
      {canNavigate ? (
        <button type="button" className={styles.UiConfigDataFlowPanel_WarningAction} onClick={onNavigate}>
          Show in graph
        </button>
      ) : null}
    </div>
  )
}

export default function UiConfigDataFlowPanel({ uid }: Props): ReactElement {
  const snap = useSnapshot(uiConfigStore)
  const rawStoreConfig = snap.uiConfigs[uid]
  const previewConfig = snap.previewCollection[uid]
  const { baseUiConfig } = snap

  // biome-ignore lint/correctness/useExhaustiveDependencies: getParsedUiConfig depends on rawStoreConfig, previewConfig, baseUiConfig, and uid — using those stable store-slice refs instead of `snap` to prevent graph rebuild on local state changes (e.g. filter typing)
  const resolvedUiConfig = useMemo(
    () => snap.getParsedUiConfig(uid),
    [rawStoreConfig, previewConfig, baseUiConfig, uid]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: getUnparsedUiConfig depends on rawStoreConfig and previewConfig (no base config) — using those stable store-slice refs instead of `snap` to prevent graph rebuild on local state changes (e.g. filter typing)
  const rawUiConfig = useMemo(() => snap.getUnparsedUiConfig(uid), [rawStoreConfig, previewConfig, uid])

  const [filterText, setFilterText] = useState('')
  const [isIssuesFilterActive, setIsIssuesFilterActive] = useState(true)
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null)
  const [expandedIssueRowIds, setExpandedIssueRowIds] = useState<Set<string>>(new Set())
  const topAnchorRef = useRef<HTMLDivElement | null>(null)
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})
  const edgesSectionRef = useRef<HTMLElement | null>(null)
  const dependenciesSectionRef = useRef<HTMLElement | null>(null)
  const warningsSectionRef = useRef<HTMLElement | null>(null)

  const graph = useMemo(() => {
    if (!resolvedUiConfig) {
      return null
    }

    return buildDataFlowGraph(resolvedUiConfig, {
      rawUiConfig,
      baseUiConfig,
    })
  }, [resolvedUiConfig, rawUiConfig, baseUiConfig])

  const normalizedFilter = filterText.trim().toLowerCase()
  const edgeRows = useMemo(() => {
    if (!graph) {
      return []
    }

    return graph.edges.map((edge) => ({
      id: `edge:${edge.itemDataId ?? '(none)'}->${edge.dsiReturnId ?? '(none)'}`,
      edge,
    }))
  }, [graph])

  const dependencyRows = useMemo(() => {
    if (!graph) {
      return []
    }

    return graph.calcDependencies.map((dependency) => ({
      id: `dep:${dependency.fromReturnId}->${dependency.toReturnId}`,
      dependency,
    }))
  }, [graph])

  const warningsByRowId = useMemo(() => {
    const warningsMap = new Map<string, DataFlowWarning[]>()

    if (!graph) {
      return warningsMap
    }

    for (const warning of graph.warnings) {
      const targetRowId = getWarningTargetRowId(warning, edgeRows, dependencyRows)
      if (!targetRowId) {
        continue
      }

      const existingWarnings = warningsMap.get(targetRowId) ?? []
      warningsMap.set(targetRowId, [...existingWarnings, warning])
    }

    return warningsMap
  }, [dependencyRows, edgeRows, graph])

  const sortedEdgeRows = useMemo(() => {
    return [...edgeRows].sort((left, right) => {
      const leftWarnings = warningsByRowId.get(left.id) ?? []
      const rightWarnings = warningsByRowId.get(right.id) ?? []

      const leftHasWarnings = leftWarnings.length > 0
      const rightHasWarnings = rightWarnings.length > 0

      if (leftHasWarnings !== rightHasWarnings) {
        return leftHasWarnings ? -1 : 1
      }

      const warningPriorityDiff = getWarningsPriority(leftWarnings) - getWarningsPriority(rightWarnings)
      if (warningPriorityDiff !== 0) {
        return warningPriorityDiff
      }

      const statusPriorityDiff = getEdgeStatusPriority(left.edge) - getEdgeStatusPriority(right.edge)
      if (statusPriorityDiff !== 0) {
        return statusPriorityDiff
      }

      const leftKey = `${left.edge.itemDataId ?? ''}-${left.edge.dsiReturnId ?? ''}`
      const rightKey = `${right.edge.itemDataId ?? ''}-${right.edge.dsiReturnId ?? ''}`
      return leftKey.localeCompare(rightKey)
    })
  }, [edgeRows, warningsByRowId])

  const sortedDependencyRows = useMemo(() => {
    return [...dependencyRows].sort((left, right) => {
      const leftWarnings = warningsByRowId.get(left.id) ?? []
      const rightWarnings = warningsByRowId.get(right.id) ?? []

      const leftHasWarnings = leftWarnings.length > 0
      const rightHasWarnings = rightWarnings.length > 0

      if (leftHasWarnings !== rightHasWarnings) {
        return leftHasWarnings ? -1 : 1
      }

      const warningPriorityDiff = getWarningsPriority(leftWarnings) - getWarningsPriority(rightWarnings)
      if (warningPriorityDiff !== 0) {
        return warningPriorityDiff
      }

      const statusPriorityDiff =
        getDependencyStatusPriority(left.dependency) - getDependencyStatusPriority(right.dependency)
      if (statusPriorityDiff !== 0) {
        return statusPriorityDiff
      }

      const leftKey = `${left.dependency.fromReturnId}-${left.dependency.toReturnId}`
      const rightKey = `${right.dependency.fromReturnId}-${right.dependency.toReturnId}`
      return leftKey.localeCompare(rightKey)
    })
  }, [dependencyRows, warningsByRowId])

  const filteredEdges = useMemo(() => {
    if (!normalizedFilter) {
      return sortedEdgeRows
    }

    return sortedEdgeRows.filter(({ id, edge }) => {
      if (edgeMatchesFilter(edge, normalizedFilter)) {
        return true
      }
      const rowWarnings = warningsByRowId.get(id) ?? []
      return rowWarnings.some((warning) => warningMatchesFilter(warning, normalizedFilter))
    })
  }, [normalizedFilter, sortedEdgeRows, warningsByRowId])

  const filteredCalcDependencies = useMemo(() => {
    if (!normalizedFilter) {
      return sortedDependencyRows
    }

    return sortedDependencyRows.filter(({ id, dependency }) => {
      if (dependencyMatchesFilter(dependency, normalizedFilter)) {
        return true
      }
      const rowWarnings = warningsByRowId.get(id) ?? []
      return rowWarnings.some((warning) => warningMatchesFilter(warning, normalizedFilter))
    })
  }, [normalizedFilter, sortedDependencyRows, warningsByRowId])

  const filteredWarnings = useMemo(() => {
    if (!graph || !normalizedFilter) {
      return graph?.warnings ?? []
    }

    return graph.warnings.filter((warning) => warningMatchesFilter(warning, normalizedFilter))
  }, [graph, normalizedFilter])

  const generalWarnings = useMemo(() => {
    return filteredWarnings.filter((warning) => getWarningTargetRowId(warning, edgeRows, dependencyRows) === null)
  }, [dependencyRows, edgeRows, filteredWarnings])

  const allGeneralWarnings = useMemo(() => {
    if (!graph) {
      return []
    }

    return graph.warnings.filter((warning) => getWarningTargetRowId(warning, edgeRows, dependencyRows) === null)
  }, [dependencyRows, edgeRows, graph])

  const inlinedIssuesCount = useMemo(() => {
    if (!graph) {
      return 0
    }

    return graph.warnings.length - allGeneralWarnings.length
  }, [allGeneralWarnings.length, graph])

  const combinedIssuesCount = inlinedIssuesCount + allGeneralWarnings.length

  const consistencySummary = useMemo(() => {
    if (!graph) {
      return {
        unresolvedItemsCount: 0,
        orphanedDsisCount: 0,
        unresolvedCalcVariablesCount: 0,
        affectedOutputsCount: 0,
      }
    }

    return buildDataFlowConsistencySummary(graph)
  }, [graph])

  const visibleEdges = useMemo(() => {
    if (!isIssuesFilterActive) {
      return filteredEdges
    }

    return filteredEdges.filter(({ id, edge }) => {
      const rowWarnings = warningsByRowId.get(id) ?? []
      return rowWarnings.length > 0 || edge.status !== 'matched'
    })
  }, [filteredEdges, isIssuesFilterActive, warningsByRowId])

  const visibleDependencies = useMemo(() => {
    if (!isIssuesFilterActive) {
      return filteredCalcDependencies
    }

    return filteredCalcDependencies.filter(({ id, dependency }) => {
      const rowWarnings = warningsByRowId.get(id) ?? []
      return rowWarnings.length > 0 || dependency.status !== 'resolved'
    })
  }, [filteredCalcDependencies, isIssuesFilterActive, warningsByRowId])

  function toggleRowIssues(rowId: string): void {
    setExpandedIssueRowIds((current) => {
      const next = new Set(current)
      if (next.has(rowId)) {
        next.delete(rowId)
      } else {
        next.add(rowId)
      }
      return next
    })
  }

  function scrollToSection(sectionRef: React.RefObject<HTMLElement | null>): void {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function toggleIssuesFilter(): void {
    setIsIssuesFilterActive((current) => !current)
  }

  function scrollToTop(): void {
    topAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function navigateToWarningTarget(warning: DataFlowWarning): void {
    const targetRowId = getWarningTargetRowId(warning, edgeRows, dependencyRows)
    if (!targetRowId) {
      return
    }

    setHighlightedRowId(targetRowId)

    const targetIsVisible =
      filteredEdges.some((row) => row.id === targetRowId) ||
      filteredCalcDependencies.some((row) => row.id === targetRowId)

    if (!targetIsVisible && filterText.length > 0) {
      setFilterText('')
    }

    requestAnimationFrame(() => {
      rowRefs.current[targetRowId]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  if (!resolvedUiConfig || !graph) {
    return (
      <div className={styles.UiConfigDataFlowPanel}>
        <div className={styles.UiConfigDataFlowPanel_Empty}>Select a config to inspect its data flow.</div>
      </div>
    )
  }

  return (
    <div className={styles.UiConfigDataFlowPanel}>
      <div ref={topAnchorRef} />

      <section className={styles.UiConfigDataFlowPanel_ConsistencySummary} aria-label="Consistency summary">
        <div className={styles.UiConfigDataFlowPanel_ConsistencySummaryTitle}>Consistency summary</div>
        <div className={styles.UiConfigDataFlowPanel_ConsistencySummaryStats}>
          <div className={styles.UiConfigDataFlowPanel_ConsistencySummaryStat}>
            <span className={styles.UiConfigDataFlowPanel_ConsistencySummaryStatLabel}>Items unresolved</span>
            <span className={styles.UiConfigDataFlowPanel_ConsistencySummaryStatValue}>
              {consistencySummary.unresolvedItemsCount}
            </span>
          </div>
          <div className={styles.UiConfigDataFlowPanel_ConsistencySummaryStat}>
            <span className={styles.UiConfigDataFlowPanel_ConsistencySummaryStatLabel}>DSIs orphaned</span>
            <span className={styles.UiConfigDataFlowPanel_ConsistencySummaryStatValue}>
              {consistencySummary.orphanedDsisCount}
            </span>
          </div>
          <div className={styles.UiConfigDataFlowPanel_ConsistencySummaryStat}>
            <span className={styles.UiConfigDataFlowPanel_ConsistencySummaryStatLabel}>Calc variables unresolved</span>
            <span className={styles.UiConfigDataFlowPanel_ConsistencySummaryStatValue}>
              {consistencySummary.unresolvedCalcVariablesCount}
            </span>
          </div>
          <div className={styles.UiConfigDataFlowPanel_ConsistencySummaryStat}>
            <span className={styles.UiConfigDataFlowPanel_ConsistencySummaryStatLabel}>Affected outputs</span>
            <span className={styles.UiConfigDataFlowPanel_ConsistencySummaryStatValue}>
              {consistencySummary.affectedOutputsCount}
            </span>
          </div>
        </div>
      </section>

      <div className={styles.UiConfigDataFlowPanel_Summary}>
        <button
          type="button"
          className={styles.UiConfigDataFlowPanel_SummaryBadge}
          onClick={() => scrollToSection(edgesSectionRef)}
        >
          {visibleEdges.length}/{edgeRows.length} edges
        </button>
        <button
          type="button"
          className={styles.UiConfigDataFlowPanel_SummaryBadge}
          onClick={() => scrollToSection(dependenciesSectionRef)}
        >
          {visibleDependencies.length}/{dependencyRows.length} calc deps
        </button>
        {combinedIssuesCount > 0 ? (
          <button
            type="button"
            className={`${styles.UiConfigDataFlowPanel_SummaryBadge} ${isIssuesFilterActive ? styles.UiConfigDataFlowPanel_SummaryBadge__active : ''}`}
            onClick={toggleIssuesFilter}
          >
            {getIssuesBadgeLabel(combinedIssuesCount)}
          </button>
        ) : null}
      </div>

      <SearchField
        label="Filter data flow"
        value={filterText}
        onChange={(value) => setFilterText(String(value))}
        onClear={() => setFilterText('')}
      />

      <section className={styles.UiConfigDataFlowPanel_Card} aria-label="Data flow edges" ref={edgesSectionRef}>
        <div className={styles.UiConfigDataFlowPanel_CardHeader}>
          <div className={styles.UiConfigDataFlowPanel_CardTitle}>Item to dataset wiring</div>
          <div className={styles.UiConfigDataFlowPanel_CardHint}>Resolved item.data_id mapped to DSI return_id</div>
        </div>

        {graph.edges.length === 0 ? (
          <div className={styles.UiConfigDataFlowPanel_NoWarnings}>
            No items or dataset instructions found on this config.
          </div>
        ) : visibleEdges.length === 0 ? (
          <div className={styles.UiConfigDataFlowPanel_NoWarnings}>No edge rows match the current filter.</div>
        ) : (
          <div className={styles.UiConfigDataFlowPanel_TableWrap}>
            <table className={styles.UiConfigDataFlowPanel_Table}>
              <thead>
                <tr>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Item</th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}></th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Dataset instruction</th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Type</th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Source</th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleEdges.map(({ id, edge }) => {
                  const rowWarnings = warningsByRowId.get(id) ?? []
                  const isExpanded = expandedIssueRowIds.has(id)

                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`${styles.UiConfigDataFlowPanel_TableRow} ${highlightedRowId === id ? styles.UiConfigDataFlowPanel_TableRow__highlighted : ''}`}
                        ref={(element) => {
                          rowRefs.current[id] = element
                        }}
                        data-row-id={id}
                        data-highlighted={highlightedRowId === id ? 'true' : 'false'}
                      >
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>
                          {renderEdgeSide(edge.itemDataId, edge.itemTitle)}
                        </td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>
                          <span className={styles.UiConfigDataFlowPanel_Arrow}>→</span>
                        </td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>{renderEdgeSide(edge.dsiReturnId)}</td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>{edge.dsiType ?? '—'}</td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>{getSourceLabel(edge.source)}</td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>
                          <span
                            className={`${styles.UiConfigDataFlowPanel_StatusBadge} ${getEdgeStatusClassName(edge)}`}
                          >
                            {getEdgeStatusLabel(edge)}
                          </span>
                          {rowWarnings.length > 0 ? (
                            <button
                              type="button"
                              className={styles.UiConfigDataFlowPanel_RowIssuesToggle}
                              onClick={() => toggleRowIssues(id)}
                            >
                              {isExpanded ? 'Hide issues' : `Issues (${rowWarnings.length})`}
                            </button>
                          ) : null}
                        </td>
                      </tr>

                      {rowWarnings.length > 0 && isExpanded ? (
                        <tr className={styles.UiConfigDataFlowPanel_TableDetailRow} data-testid={`row-issues-${id}`}>
                          <td className={styles.UiConfigDataFlowPanel_TableCell} colSpan={6}>
                            <div className={styles.UiConfigDataFlowPanel_RowIssuesList}>
                              {rowWarnings.map((warning, warningIndex) => (
                                <div
                                  key={`${id}-warning-${warningIndex}`}
                                  className={styles.UiConfigDataFlowPanel_RowIssueItem}
                                >
                                  <span className={styles.UiConfigDataFlowPanel_RowIssueMeta}>
                                    {warning.kind.replaceAll('_', ' ')} · {getWarningClassificationLabel(warning)}
                                  </span>
                                  <span>{warning.message}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
            <button type="button" className={styles.UiConfigDataFlowPanel_ScrollTopLink} onClick={scrollToTop}>
              Scroll to top
            </button>
          </div>
        )}
      </section>

      <section
        className={styles.UiConfigDataFlowPanel_Card}
        aria-label="Calculation dependencies"
        ref={dependenciesSectionRef}
      >
        <div className={styles.UiConfigDataFlowPanel_CardHeader}>
          <div className={styles.UiConfigDataFlowPanel_CardTitle}>Calculation dependencies</div>
          <div className={styles.UiConfigDataFlowPanel_CardHint}>String variables feeding resolved calc outputs</div>
        </div>

        {graph.calcDependencies.length === 0 ? (
          <div className={styles.UiConfigDataFlowPanel_NoWarnings}>No calc dependency edges found.</div>
        ) : visibleDependencies.length === 0 ? (
          <div className={styles.UiConfigDataFlowPanel_NoWarnings}>
            No calc dependency rows match the current filter.
          </div>
        ) : (
          <div className={styles.UiConfigDataFlowPanel_TableWrap}>
            <table className={styles.UiConfigDataFlowPanel_Table}>
              <thead>
                <tr>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Dependency</th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}></th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Calc output</th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Source</th>
                  <th className={styles.UiConfigDataFlowPanel_TableHeadCell}>Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleDependencies.map(({ id, dependency }) => {
                  const rowWarnings = warningsByRowId.get(id) ?? []
                  const isExpanded = expandedIssueRowIds.has(id)

                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`${styles.UiConfigDataFlowPanel_TableRow} ${highlightedRowId === id ? styles.UiConfigDataFlowPanel_TableRow__highlighted : ''}`}
                        ref={(element) => {
                          rowRefs.current[id] = element
                        }}
                        data-row-id={id}
                        data-highlighted={highlightedRowId === id ? 'true' : 'false'}
                      >
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>
                          {renderEdgeSide(dependency.fromReturnId)}
                        </td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>
                          <span className={styles.UiConfigDataFlowPanel_Arrow}>→</span>
                        </td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>
                          {renderEdgeSide(dependency.toReturnId)}
                        </td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>
                          {getSourceLabel(dependency.fromSource)}
                        </td>
                        <td className={styles.UiConfigDataFlowPanel_TableCell}>
                          <span
                            className={`${styles.UiConfigDataFlowPanel_StatusBadge} ${getDependencyStatusClassName(dependency)}`}
                          >
                            {dependency.status}
                          </span>
                          {rowWarnings.length > 0 ? (
                            <button
                              type="button"
                              className={styles.UiConfigDataFlowPanel_RowIssuesToggle}
                              onClick={() => toggleRowIssues(id)}
                            >
                              {isExpanded ? 'Hide issues' : `Issues (${rowWarnings.length})`}
                            </button>
                          ) : null}
                        </td>
                      </tr>

                      {rowWarnings.length > 0 && isExpanded ? (
                        <tr className={styles.UiConfigDataFlowPanel_TableDetailRow} data-testid={`row-issues-${id}`}>
                          <td className={styles.UiConfigDataFlowPanel_TableCell} colSpan={5}>
                            <div className={styles.UiConfigDataFlowPanel_RowIssuesList}>
                              {rowWarnings.map((warning, warningIndex) => (
                                <div
                                  key={`${id}-warning-${warningIndex}`}
                                  className={styles.UiConfigDataFlowPanel_RowIssueItem}
                                >
                                  <span className={styles.UiConfigDataFlowPanel_RowIssueMeta}>
                                    {warning.kind.replaceAll('_', ' ')} · {getWarningClassificationLabel(warning)}
                                  </span>
                                  <span>{warning.message}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
            <button type="button" className={styles.UiConfigDataFlowPanel_ScrollTopLink} onClick={scrollToTop}>
              Scroll to top
            </button>
          </div>
        )}
      </section>

      {allGeneralWarnings.length > 0 ? (
        <section className={styles.UiConfigDataFlowPanel_Card} aria-label="General issues" ref={warningsSectionRef}>
          <div className={styles.UiConfigDataFlowPanel_CardHeader}>
            <div className={styles.UiConfigDataFlowPanel_CardTitle}>General issues</div>
            <div className={styles.UiConfigDataFlowPanel_CardHint}>
              Issues not tied to a single edge or dependency row
            </div>
          </div>

          {generalWarnings.length === 0 ? (
            <div className={styles.UiConfigDataFlowPanel_NoWarnings}>No general issues match the current filter.</div>
          ) : (
            <div className={styles.UiConfigDataFlowPanel_Warnings}>
              {generalWarnings.map((warning, index) => (
                <WarningItem
                  key={`${warning.kind}-${warning.context.returnId ?? warning.context.dataId ?? index}`}
                  warning={warning}
                  canNavigate={getWarningTargetRowId(warning, edgeRows, dependencyRows) !== null}
                  onNavigate={() => navigateToWarningTarget(warning)}
                />
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  )
}
